import { Injectable, inject } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { Company } from '@bdeo-motor/smartphoto-entities';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { AuthService } from './auth.service';
import { ISelector } from '@bdeo-platform/private-api';
import { CardField } from '../entities/types/smartphoto-card-field.type';
import { InfoField } from '../entities/types/smartphoto-info-field.type';
import { BdeoSelectOption } from '@bdeo/webcomponents';

export type SelectorItems =
  | (ISelector | undefined)[]
  | Partial<{
      [key: string]: unknown;
    }>;

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly apiClientService = inject(ApiClientService);
  private readonly authService = inject(AuthService);
  private readonly company$ = new BehaviorSubject<Company | undefined>(undefined);
  private company?: Company;

  public readonly hasDeadline$ = this.company$.pipe(map((company) => company?.deadlineSelfadjust));
  public readonly hasAdditionals$ = this.company$.pipe(map((company) => company?.smartphoto?.additionals));
  public readonly hasGoToAdditionalsButton$ = this.company$.pipe(map((company) => company?.brokersInfo?.showGoToAdditionalsButton));
  public readonly cardFields$: Observable<CardField[] | undefined> = this.company$.pipe(map((company) => company?.brokersInfo?.cardFields));
  public readonly infoFields$: Observable<InfoField[] | undefined> = this.company$.pipe(map((company) => company?.brokersInfo?.infoFields));
  public readonly createFields$: Observable<Company['createFields'] | undefined> = this.company$.pipe(
    map((company) => company?.createFields)
  );
  public readonly useConditionsSelfadjust$ = this.company$.pipe(map((company) => company?.useConditionsSelfadjust));

  public async getCompany(): Promise<Company | undefined> {
    try {
      if (this.company) {
        return this.company;
      }

      const companyId = localStorage.getItem('company_id');

      if (!companyId) {
        return;
      }

      const response = await this.apiClientService.getApiClient().motor.company.get({
        recordId: companyId,
        access_token: this.authService.getAccessToken()
      });

      const {
        data: { success, entity }
      } = response;

      if (!success) {
        this.authService.logOut();
        return;
      }

      this.company = new Company(entity);
      this.company$.next(this.company);
      this.configureCompanyLanguage(this.company);

      return this.company;
    } catch (e: unknown) {
      if (e instanceof this.apiClientService.getApiClient().motor.company.get.Error) {
        const error = e.getActualType();

        if (error.status === 400) {
          this.authService.logOut();
        }

        if (error.status === 401) {
          this.authService.logOut();
        }
      }

      throw e;
    }
  }

  private configureCompanyLanguage(company: Company): void {
    const languageEvent = {
      detail: {
        language: company.fixedLanguage ?? navigator.language ?? 'en-US',
        customLabels: company.labels
      }
    };

    window.dispatchEvent(new CustomEvent('languageChanged', languageEvent));
  }

  public getCurrentCompany(): Company | undefined {
    return this.company;
  }

  public getCurrentCompanyObservable(): Observable<Company | undefined> {
    return this.company$.asObservable();
  }

  public getNavBarLogo(): string {
    return this.company?.styles?.selfadjust?.images?.logo_brokersNavbar ?? '';
  }

  // TO DO: Definir comportamiento ante !success y error 400
  public async getSelector(name: string): Promise<SelectorItems> {
    try {
      const response = await this.apiClientService.getApiClient().motor.shared.selector.get({ type: name });

      const {
        data: { success, items }
      } = response;

      if (!success) {
        // TODO handle a not success call
      }

      return items;
    } catch (e: unknown) {
      if (e instanceof this.apiClientService.getApiClient().motor.shared.selector.get.Error) {
        const error = e.getActualType();

        if (error.status === 400) {
          // TODO handle 400
        }
      }

      throw e;
    }
  }

  public async getSelectorValues(selectorID: string): Promise<BdeoSelectOption[]> {
    const rawSelectorValues = await this.getSelector(selectorID);

    if (!Array.isArray(rawSelectorValues)) {
      throw new Error('Unexpected selector format');
    }

    return rawSelectorValues.map((option) => ({
      value: option?.value,
      text: option?.name ?? '',
      disabled: false,
      selected: false
    }));
  }
}
